// ============================================================
// nucleo/libro.jsx — la existencia no es un número, es la suma del libro.
//
// POR QUÉ ESTO EXISTE. Guardar "quedan 8" parece lo obvio y es justo lo que
// no funciona cuando hay dos celulares vendiendo sin señal:
//
//   teléfono de Danny     teléfono del vendedor
//   8 - 2 = 6             8 - 1 = 7
//        ↓ los dos suben su número ↓
//            el último en llegar gana
//            una de las dos ventas DESAPARECE
//
// Con el libro, cada uno agrega su renglón —"salieron 2", "salió 1"— y al
// juntarse los dos cuentan: 8 - 2 - 1 = 5. Nadie pisa a nadie, porque nadie
// escribe el total: el total se calcula.
//
// Es la misma forma que mn.movimiento en base/inventario.sql, A PROPÓSITO.
// Lo que el teléfono guarda y lo que el servidor guarda tienen que ser la
// misma cosa, o la sincronización se convierte en una traducción, y una
// traducción es un lugar donde se pierden ventas.
//
// EL NÚMERO NO DESAPARECE: se queda como caché, igual que mn.saldo del lado
// del servidor. Las pantallas siguen leyendo p.stock como siempre; lo que
// cambia es quién lo escribe. Antes lo escribía cada pantalla; ahora sale de
// aquí, de un solo lugar, y se puede volver a calcular desde el libro si
// alguna vez no cuadra.
// ============================================================

import { nuevoId, esId } from "./ids.jsx";
import { bitacora } from "./bitacora.jsx";
import { stockDe, apartadoDe, tieneVariantes } from "./contrato.jsx";

// Los mismos ocho motivos del esquema, ni uno más. Si aquí se inventa uno
// que allá no existe, el servidor lo rechaza y la venta se queda atorada
// para siempre sin que nadie sepa por qué.
export const MOTIVOS = ["inicial", "entrada", "venta", "apartado", "entrega",
                        "devolucion", "ajuste", "merma"];

// Cuántos movimientos ya subidos se guardan en el teléfono antes de podar.
// Los de arriba viven en el servidor, que es el archivo de verdad.
// LOS QUE NO SE HAN SUBIDO NO SE PODAN NUNCA, pase lo que pase: podar uno
// sin subir es borrar una venta.
export const PODAR_EN = 2000;

// ------------------------------------------------------------
// La hoja: dónde vive de verdad la existencia. La variante si el producto
// tiene colores o tonos, y el producto mismo si no. Igual que el hoja_id
// generado de mn.saldo.
// ------------------------------------------------------------
export const hojaDe = (m) => m.variante_id || m.producto_id;

export function libroVacio() {
  return {
    // El punto de partida. Existe para poder podar: después de borrar los
    // movimientos viejos, el libro ya no alcanza a recalcular todo solo, así
    // que lo de antes queda aquí resumido.
    base: {},
    movs: [],      // el libro, en el orden en que llegaron
    saldos: {},    // el caché: { [hoja]: { stock, apartado } }
    ids: {},       // qué identificadores ya entraron, para no contar doble
  };
}

// ------------------------------------------------------------
// Un movimiento nuevo. El identificador lo genera ESTE teléfono y es la
// llave: si la venta se sube dos veces porque se cortó la señal al
// confirmar, el servidor la reconoce por el id y no descuenta de nuevo.
// ------------------------------------------------------------
export function nuevoMov({ producto_id, variante_id = null, delta_stock = 0,
  delta_apartado = 0, motivo, ref_id = null, cantidad = null, nota = null,
  dispositivo = null, ocurrio = null }) {
  return {
    id: nuevoId(),
    producto_id, variante_id,
    delta_stock: Math.trunc(Number(delta_stock) || 0),
    delta_apartado: Math.trunc(Number(delta_apartado) || 0),
    motivo, ref_id, cantidad, nota,
    dispositivo: dispositivo || "este-telefono",
    // CUÁNDO PASÓ EN EL MUNDO, no cuándo se subió. Una venta del domingo sin
    // señal que se sube el lunes ocurrió el domingo, y así tiene que salir
    // en el corte del domingo.
    ocurrio: ocurrio || new Date().toISOString(),
    subido: false,
  };
}

// Lo que el servidor acepta. Si algo de aquí no cuadra, es mejor saberlo en
// el teléfono que descubrirlo cuando la cola se atora.
export function validarMov(m) {
  const malo = [];
  if (!m || typeof m !== "object") return ["no es un movimiento"];
  if (!esId(m.id)) malo.push("el identificador no tiene forma de identificador");
  if (!m.producto_id) malo.push("falta de qué producto es");
  if (!MOTIVOS.includes(m.motivo)) malo.push(`el motivo "${m.motivo}" no existe`);
  if (!m.ocurrio || Number.isNaN(new Date(m.ocurrio).getTime())) malo.push("falta cuándo ocurrió");
  // Un movimiento que no mueve nada no es un movimiento: es basura que
  // engorda el libro y confunde los cortes.
  if (!m.delta_stock && !m.delta_apartado) malo.push("no mueve ni existencia ni apartado");
  return malo;
}

// ------------------------------------------------------------
// Meter un movimiento al libro.
//
// ES IDEMPOTENTE: si el id ya estaba, no hace nada y lo dice. De eso depende
// todo lo demás. El mismo movimiento puede llegar dos veces por tres
// caminos distintos —se reintentó al subir, el otro celular lo bajó, se
// restauró un respaldo— y en ninguno debe descontar doble.
// ------------------------------------------------------------
export function meter(libro, mov) {
  const malo = validarMov(mov);
  if (malo.length) {
    bitacora.error("libro", "movimiento rechazado", null, { porque: malo, motivo: mov?.motivo });
    return { libro, entro: false, porque: malo };
  }
  if (libro.ids[mov.id]) return { libro, entro: false, repetido: true };

  const hoja = hojaDe(mov);
  const antes = libro.saldos[hoja] || { stock: 0, apartado: 0 };
  return {
    libro: {
      ...libro,
      movs: [...libro.movs, mov],
      ids: { ...libro.ids, [mov.id]: 1 },
      saldos: { ...libro.saldos, [hoja]: {
        stock: antes.stock + mov.delta_stock,
        apartado: antes.apartado + mov.delta_apartado,
      } },
    },
    entro: true,
  };
}

// Varios de un golpe, por ejemplo las líneas de una venta o lo que bajó del
// servidor. Devuelve el libro y la cuenta de lo que pasó con cada uno.
export function meterVarios(libro, movs) {
  let L = libro, entraron = 0, repetidos = 0;
  const rechazados = [];
  for (const m of movs || []) {
    const r = meter(L, m);
    L = r.libro;
    if (r.entro) entraron++;
    else if (r.repetido) repetidos++;
    else rechazados.push({ id: m?.id, porque: r.porque });
  }
  return { libro: L, entraron, repetidos, rechazados };
}

// ------------------------------------------------------------
// Los saldos calculados desde cero. Esta es LA VERDAD; el caché es nomás
// para leer rápido. Sirve para revisar que el caché no se haya desviado.
// ------------------------------------------------------------
export function saldosDe(libro) {
  const s = {};
  for (const [hoja, v] of Object.entries(libro.base || {})) {
    s[hoja] = { stock: v.stock || 0, apartado: v.apartado || 0 };
  }
  for (const m of libro.movs || []) {
    const hoja = hojaDe(m);
    if (!s[hoja]) s[hoja] = { stock: 0, apartado: 0 };
    s[hoja].stock += m.delta_stock;
    s[hoja].apartado += m.delta_apartado;
  }
  return s;
}

// ¿El caché cuadra con el libro? Equivale a la vista mn.revision del
// servidor. Devuelve las hojas que no cuadran; vacío es que todo está bien.
export function revisar(libro) {
  const real = saldosDe(libro);
  const hojas = new Set([...Object.keys(real), ...Object.keys(libro.saldos || {})]);
  const malas = [];
  for (const h of hojas) {
    const a = real[h] || { stock: 0, apartado: 0 };
    const b = (libro.saldos || {})[h] || { stock: 0, apartado: 0 };
    if (a.stock !== b.stock || a.apartado !== b.apartado) {
      malas.push({ hoja: h, libro: a, cache: b });
    }
  }
  return malas;
}

// Si el caché se desvió, se tira y se vuelve a calcular. El libro manda.
export function recalcular(libro) {
  const saldos = saldosDe(libro);
  bitacora.aviso("libro", "caché recalculado desde el libro", { hojas: Object.keys(saldos).length });
  return { ...libro, saldos };
}

// ------------------------------------------------------------
// Podar: tirar los movimientos viejos QUE YA ESTÁN EN EL SERVIDOR, dejando
// su efecto resumido en "base". El teléfono no es el archivo; el servidor
// sí.
//
// La regla que no se rompe: un movimiento sin subir no se poda nunca. Si se
// podara, esa venta se borraría del único lugar donde existe.
// ------------------------------------------------------------
export function podar(libro, dejar = PODAR_EN) {
  if ((libro.movs || []).length <= dejar) return libro;

  // Se recorre desde el principio y se corta en el primer movimiento sin
  // subir: de ahí en adelante no se toca nada, aunque sobren.
  let corte = 0;
  const sobran = libro.movs.length - dejar;
  while (corte < sobran && libro.movs[corte].subido) corte++;
  if (corte === 0) return libro;

  const base = {};
  for (const [h, v] of Object.entries(libro.base || {})) base[h] = { ...v };
  const ids = { ...libro.ids };
  for (const m of libro.movs.slice(0, corte)) {
    const h = hojaDe(m);
    if (!base[h]) base[h] = { stock: 0, apartado: 0 };
    base[h].stock += m.delta_stock;
    base[h].apartado += m.delta_apartado;
    // El id se olvida junto con el movimiento. Volver a recibirlo después
    // sería raro —el servidor ya lo tiene— y si pasara, el saldo ya quedó
    // metido en base, así que lo peor es un renglón repetido en pantalla,
    // no un descuento doble del total.
    delete ids[m.id];
  }
  bitacora.paso("libro", "libro podado", { tirados: corte, quedan: libro.movs.length - corte });
  return { ...libro, base, ids, movs: libro.movs.slice(corte) };
}

export const sinSubir = (libro) => (libro.movs || []).filter((m) => !m.subido);

export function marcarSubidos(libro, ids) {
  const set = new Set(ids || []);
  if (!set.size) return libro;
  return { ...libro, movs: libro.movs.map((m) => set.has(m.id) ? { ...m, subido: true } : m) };
}

// ------------------------------------------------------------
// De números a libro: la migración, una sola vez.
//
// El inventario que el comerciante ya capturó son números sueltos, sin
// historia. No se tira ni se le inventa un pasado: entra como un movimiento
// "inicial" por hoja, que dice "aquí empezamos con tantos". A partir de ahí
// todo lo demás es historia de verdad.
// ------------------------------------------------------------
export function arranque(productos, libro = libroVacio()) {
  const movs = [];
  for (const p of productos || []) {
    if (tieneVariantes(p)) {
      for (const v of p.variantes) {
        const s = Number(v.stock) || 0, a = Number(v.apartado) || 0;
        if (!s && !a) continue;
        movs.push(nuevoMov({ producto_id: p.id, variante_id: v.id, delta_stock: s,
          delta_apartado: a, motivo: "inicial", nota: "existencia que ya tenías capturada" }));
      }
    } else {
      const s = stockDe(p), a = apartadoDe(p);
      if (!s && !a) continue;
      movs.push(nuevoMov({ producto_id: p.id, delta_stock: s, delta_apartado: a,
        motivo: "inicial", nota: "existencia que ya tenías capturada" }));
    }
  }
  const r = meterVarios(libro, movs);
  bitacora.paso("libro", "arranque del libro", { hojas: r.entraron });
  return r.libro;
}

// ------------------------------------------------------------
// Los movimientos de cada cosa que pasa en el puesto.
//
// Aquí está, en un solo lugar, qué le mueve a qué. Es la misma tabla que el
// comentario de base/inventario.sql, y tiene que seguir siendo la misma:
//
//   vender de contado    existencia -1   apartado  0
//   fiar                 existencia -1   apartado  0   (ya se lo llevó)
//   apartar              existencia  0   apartado +1   (no se lo llevó)
//   entregar lo apartado existencia -1   apartado -1
//   recibir mercancía    existencia +N   apartado  0
// ------------------------------------------------------------
const lineasAMovs = (lineas, hacer) =>
  (lineas || []).flatMap((l) => {
    const cant = Math.abs(Math.trunc(Number(l.cant) || 0));
    if (!cant) return [];
    return [hacer(l, cant)];
  });

export function movsDeVenta(venta, ref) {
  const apartando = venta.forma === "apartado";
  return lineasAMovs(venta.lineas, (l, cant) => nuevoMov({
    producto_id: l.id, variante_id: l.varId || null,
    delta_stock: apartando ? 0 : -cant,
    delta_apartado: apartando ? cant : 0,
    motivo: apartando ? "apartado" : "venta",
    ref_id: ref || null,
  }));
}

export function movsDeEntrega(cuenta) {
  // Sale del puesto Y se libera la reserva, en un solo renglón: si fueran
  // dos y sólo uno llegara al servidor, la mercancía quedaría apartada para
  // alguien que ya se la llevó.
  return lineasAMovs(cuenta.lineas, (l, cant) => nuevoMov({
    producto_id: l.id, variante_id: l.varId || null,
    delta_stock: -cant, delta_apartado: -cant,
    motivo: "entrega", ref_id: cuenta.id || null,
  }));
}

export function movDeEntrada({ producto_id, variante_id = null, cant, nota = null }) {
  return nuevoMov({ producto_id, variante_id, delta_stock: Math.abs(Math.trunc(cant)) || 0,
    motivo: "entrada", nota });
}

// Corrección a mano: el comerciante contó y dice "hay 7, no 9". No se
// escribe 7 encima: se apunta el -2 que explica la diferencia. Así el día
// que no cuadre se puede ver quién lo movió y cuándo.
export function movDeAjuste({ producto_id, variante_id = null, deHay, aHay, nota = null }) {
  const d = Math.trunc(Number(aHay) || 0) - Math.trunc(Number(deHay) || 0);
  if (!d) return null;
  return nuevoMov({ producto_id, variante_id, delta_stock: d, motivo: "ajuste",
    nota: nota || `contado a mano: estaba en ${deHay}, quedó en ${aHay}` });
}

// ------------------------------------------------------------
// Proyectar el libro sobre los productos: el caché que leen las pantallas.
//
// Ninguna pantalla cambió para esto. Siguen leyendo p.stock y stockDe(p)
// como siempre; lo único distinto es que ese número ya no lo escribe cada
// pantalla por su cuenta, sale de aquí.
// ------------------------------------------------------------
export function proyectar(productos, libro) {
  const s = libro.saldos || {};
  const de = (hoja) => s[hoja] || { stock: 0, apartado: 0 };
  return (productos || []).map((p) => {
    if (tieneVariantes(p)) {
      return { ...p, variantes: p.variantes.map((v) => {
        const x = de(v.id);
        return { ...v, stock: Math.max(0, x.stock), apartado: Math.max(0, x.apartado) };
      }) };
    }
    const x = de(p.id);
    return { ...p, stock: Math.max(0, x.stock), apartado: Math.max(0, x.apartado) };
  });
}

// Lo que el libro dice que hay, sin tocar los productos. Para cuando hace
// falta el número de verdad y no el recortado a cero de la pantalla: un
// sobregiro real tiene que poder verse en negativo.
export function existenciaDe(libro, hoja) {
  const x = (libro.saldos || {})[hoja] || { stock: 0, apartado: 0 };
  return { ...x, disponible: x.stock - x.apartado };
}

export const moduloLibro = {
  id: "libro", nombre: "Libro de movimientos", version: "1.0", tipo: "nucleo",
  orden: 5, requiere: ["ids"],
  probar: () => {
    const P = "11111111-1111-4111-8111-111111111111";
    const V = "22222222-2222-4222-8222-222222222222";

    // Entra mercancía y se vende: 10 - 2 = 8.
    let L = libroVacio();
    L = meter(L, movDeEntrada({ producto_id: P, variante_id: V, cant: 10 })).libro;
    const venta = movsDeVenta({ forma: "contado", lineas: [{ id: P, varId: V, cant: 2 }] }, "r1");
    L = meterVarios(L, venta).libro;
    if (existenciaDe(L, V).stock !== 8) return "10 menos 2 no dio 8";

    // LA MISMA VENTA OTRA VEZ: se cortó la señal al confirmar. No descuenta.
    const otra = meterVarios(L, venta);
    if (otra.repetidos !== 1) return "no reconoció la venta repetida";
    if (existenciaDe(otra.libro, V).stock !== 8) return "la venta repetida descontó dos veces";

    // Dos celulares sin señal, cada uno con su venta. Al juntarse, las dos
    // cuentan: 8 - 2 - 1 = 5. Esto es todo el punto del libro.
    const danny = movsDeVenta({ forma: "contado", lineas: [{ id: P, varId: V, cant: 2 }] }, "r2");
    const vendedor = movsDeVenta({ forma: "contado", lineas: [{ id: P, varId: V, cant: 1 }] }, "r3");
    let J = meterVarios(meterVarios(L, danny).libro, vendedor).libro;
    if (existenciaDe(J, V).stock !== 5) return `dos ventas sin señal dieron ${existenciaDe(J, V).stock}, no 5`;

    // Apartar no saca la mercancía; entregarla sí.
    let A = meter(libroVacio(), movDeEntrada({ producto_id: P, cant: 12 })).libro;
    A = meterVarios(A, movsDeVenta({ forma: "apartado", lineas: [{ id: P, cant: 3 }] }, "c1")).libro;
    let x = existenciaDe(A, P);
    if (x.stock !== 12 || x.apartado !== 3 || x.disponible !== 9)
      return `apartar quedó en ${x.stock}/${x.apartado}/${x.disponible}, debía ser 12/3/9`;
    A = meterVarios(A, movsDeEntrega({ id: "c1", lineas: [{ id: P, cant: 1 }] })).libro;
    x = existenciaDe(A, P);
    if (x.stock !== 11 || x.apartado !== 2) return `entregar quedó en ${x.stock}/${x.apartado}, debía ser 11/2`;

    // El caché cuadra con el libro, siempre.
    if (revisar(J).length) return "el caché no cuadra con el libro";
    const torcido = { ...J, saldos: { ...J.saldos, [V]: { stock: 99, apartado: 0 } } };
    if (!revisar(torcido).length) return "revisar no detecta un caché desviado";
    if (revisar(recalcular(torcido)).length) return "recalcular no arregla el caché";

    // La migración desde números.
    const M = arranque([{ id: P, stock: 0, variantes: [{ id: V, stock: 7 }] },
                        { id: "33333333-3333-4333-8333-333333333333", stock: 4 }]);
    if (existenciaDe(M, V).stock !== 7) return "el arranque no trajo la existencia de la variante";
    if (M.movs.some((m) => m.motivo !== "inicial")) return "el arranque debe ser todo inicial";

    // Un movimiento que no mueve nada se rechaza.
    if (meter(libroVacio(), nuevoMov({ producto_id: P, motivo: "venta" })).entro)
      return "aceptó un movimiento que no mueve nada";
    if (meter(libroVacio(), nuevoMov({ producto_id: P, delta_stock: 1, motivo: "inventado" })).entro)
      return "aceptó un motivo que no existe";

    // Podar: nunca se tira algo sin subir.
    let Q = libroVacio();
    for (let i = 0; i < 10; i++) Q = meter(Q, movDeEntrada({ producto_id: P, cant: 1 })).libro;
    Q = marcarSubidos(Q, Q.movs.slice(0, 4).map((m) => m.id));
    const antesStock = existenciaDe(Q, P).stock;
    Q = podar(Q, 6);
    if (Q.movs.length !== 6) return `podó a ${Q.movs.length} en vez de 6`;
    if (existenciaDe(Q, P).stock !== antesStock) return "al podar se perdió existencia";
    if (revisar(Q).length) return "después de podar el caché no cuadra con base + libro";
    // Con todo sin subir, no poda nada aunque sobre.
    let R = libroVacio();
    for (let i = 0; i < 10; i++) R = meter(R, movDeEntrada({ producto_id: P, cant: 1 })).libro;
    if (podar(R, 3).movs.length !== 10) return "podó movimientos que no se habían subido";

    // Proyectar sobre los productos, que es lo que leen las pantallas.
    const proy = proyectar([{ id: P, variantes: [{ id: V, etiqueta: "Tono 1" }] }], J);
    if (proy[0].variantes[0].stock !== 5) return "la proyección no llegó a la variante";
    return true;
  },
};
